// This layer displays a particular tile layer from scan object.
// Override method getTileUrl to specify what tiles will it show.
// Override method shouldBeVisible to specify when it should be visible.

import * as L from "leaflet";
import { IStoreState, getRouterState } from "../redux/rootReducer";

import { IBoundLayer, IBoundLayerOptions } from "./BoundLayer";
import S3TileAccessLayer from "./S3TileAccessLayer";
import { IAccessToken } from "../redux/modules/auth/reducer";
import { ensureAccessToken } from "../redux/modules/auth/asyncActions";

export default abstract class AbstractTilesLayer
  extends L.LayerGroup
  implements IBoundLayer
{
  public options: IBoundLayerOptions;
  private map: L.Map;
  private zoomLockLayer: L.Layer;
  private tilesLayer: S3TileAccessLayer;

  constructor(options: IBoundLayerOptions) {
    super([], {});
    L.Util.setOptions(this, options);
  }

  public onAdd(map: L.Map) {
    L.LayerGroup.prototype.onAdd.call(this, map);
    this.map = map;
    return this;
  }

  public onRemove(map: L.Map) {
    this.map = null;
    L.LayerGroup.prototype.onRemove.call(this, map);
    return this;
  }

  public async update(action: IAction<any>) {
    const { getState } = this.options;
    const state = getState();

    if (!this.zoomLockLayer) {
      this.lockZoomLevel(this.getLayerOptions(state).maxZoom);
    }

    await this.ensureTileLayerCreated();

    if (this.tilesLayer) {
      const opacity = this.shouldBeVisible(state) ? 1 : 0;
      this.tilesLayer.setOpacity(opacity);
    }
  }

  protected abstract getTilesPath(state: any): string;

  protected abstract shouldBeVisible(state: IStoreState): boolean;

  protected abstract getLayerOptions(state: any): L.TileLayerOptions;

  private async ensureTileLayerCreated() {
    const state = this.options.getState();
    const tilesPath = this.getTilesPath(state);
    if (!(this.tilesLayer && this.hasLayer(this.tilesLayer)) && tilesPath) {
      const { customerId, siteId } = getRouterState(state).data;
      const token = await this.options.dispatch(
        ensureAccessToken(customerId, siteId),
      );
      this.addTileLayer(tilesPath, token, state);
    }
  }

  private addTileLayer(
    tilesPath: string,
    accessToken: IAccessToken,
    state: any,
  ) {
    this.tilesLayer = new S3TileAccessLayer(
      tilesPath,
      {
        ...this.getLayerOptions(state),
        zIndex: this.options.zIndex,
      },
      accessToken,
    );
    this.tilesLayer.on("tileloadstart", this.handleTileLoadStarted);
    this.addLayer(this.tilesLayer);
  }

  private handleTileLoadStarted = async () => {
    // tile was requested. we need to check if token is not expired
    // and send update request if necessary
    const state = this.options.getState();
    const { customerId, siteId } = getRouterState(state).data;
    const token = await this.options.dispatch(
      ensureAccessToken(customerId, siteId),
    );
    this.tilesLayer.updateAccessToken(token);
  };

  private lockZoomLevel(zoomLevel: number) {
    // While updating credentials we remove our tile layer and leaflet
    // resets map zoom. To avoid it we permanently add an empty layer with
    // the same zoom level.
    this.zoomLockLayer = L.gridLayer({
      maxZoom: zoomLevel,
    });
    this.addLayer(this.zoomLockLayer);
  }
}
